import { UUID } from 'node:crypto';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IIncidentCode } from '@shared/services/incident-code-service';
import { IMesUser } from '@shared/services/mesuser.service';
import { ProductionNoteService } from '@shared/services/production-note.service';
import { IStockReorder, StockReorderService } from '@shared/services/stock-reorder.service';
import { SessionStorageService } from '@shared/services/storage.service';

export interface IStockReorderView {
	id: UUID;
	partNumber: string;
	title: string;
	handled: boolean;
	incidentCodeId: UUID;
	note?: string;
	employee?: string;
	sequenceNumber: number;
	itemAlias: string;
	batchId: UUID;
}

@Component({
	selector: 'app-update-stock-reorder',
	templateUrl: './update-stock-reorder.component.html',
	styleUrl: './update-stock-reorder.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class UpdateStockReorderComponent {
	@Output() updated: EventEmitter<any> = new EventEmitter();
	form: FormGroup;
	httpError: string = '';
	reordableSequenceNumbers: number[];
	loading: boolean = false;
	reordertypes = ['Glas', 'Alu profil', 'Andet'];
	incidentCodesByGroup: { group: string; codes: IIncidentCode[] }[] = [];
	employees: IMesUser[];
	stockReorderView: IStockReorderView;

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: IStockReorder & {
			employees: IMesUser[];
			incidentCodesByGroup: { group: string; codes: IIncidentCode[] }[];
		},
		private dialogRef: MatDialogRef<UpdateStockReorderComponent>,
		private fb: FormBuilder,
		private stockReorderService: StockReorderService,
		private productionNoteService: ProductionNoteService,
		sessionStorage: SessionStorageService,
	) {
		this.stockReorderView = {
			id: data.id,
			partNumber: data.reorderPartNumber,
			title: data.title,
			handled: data.handled,
			incidentCodeId: data.incidentCodeId,
			note: '',
			employee: sessionStorage.get('User.Email'),
			itemAlias: data.itemAlias,
			sequenceNumber: data.sequenceNumber,
			batchId: data.batchId,
		};

		// Assign injected data (always provided)
		this.employees = data.employees;
		this.incidentCodesByGroup = data.incidentCodesByGroup;
	}

	ngOnInit(): void {
		this.initializeForm();
	}

	initializeForm(): void {
		this.form = this.fb.group({
			partNumber: [this.stockReorderView.partNumber, [Validators.required]],
			title: [this.stockReorderView.title, [Validators.required]],
			handled: [this.stockReorderView.handled],
			incidentCodeId: [this.stockReorderView.incidentCodeId, [Validators.required]],
			note: [this.stockReorderView.note],
		});
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.stockReorderView) {
					this.stockReorderView[key] = value[key];
				}
			});
		});
	}

	onSave(): void {
		if (!this.form.valid) return;

		if (this.loading) return;

		this.loading = true;

		// Create production note in case this field has been set
		if (this.stockReorderView.note?.length) {
			this.productionNoteService
				.create({
					createdBy: this.stockReorderView.employee,
					itemAlias: this.stockReorderView.itemAlias,
					note: this.stockReorderView.note,
					sequenceNumber: this.stockReorderView.sequenceNumber,
					batchId: this.stockReorderView.batchId,
				})
				.subscribe();
		}

		const id: UUID = this.stockReorderView.id;
		this.stockReorderService
			.updateStockReorder(id, {
				handled: this.stockReorderView.handled,
				incidentCodeId: this.stockReorderView.incidentCodeId,
				reorderPartNumber: this.stockReorderView.partNumber,
				title: this.stockReorderView.title,
			})
			.subscribe({
				next: () => {
					this.updated.emit();
					this.dialogRef.close();
				},
				error: (errorResponse) => {
					this.httpError = `${errorResponse.status}`;
					this.loading = false;
				},
			});
	}

	onCancel = () => this.dialogRef.close();

	get controls() {
		return this.form.controls;
	}
}
